import React from 'react';
import { Platform, TouchableOpacity, View, StyleSheet } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Ionicons } from '@expo/vector-icons';
import ThemedText from './ThemedText';
import { useTheme } from '../../theme/ThemeContext';

// @ts-ignore
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  label: string;
  helperText?: string;
  minimumDate?: Date;
  maximumDate?: Date;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label,
  helperText,
  minimumDate,
  maximumDate,
}) => {
  const { theme } = useTheme();
  const [showPicker, setShowPicker] = React.useState(false);

  const handleWebDateChange = (event: any) => {
    const dateString = event.target.value;
    if (dateString) {
      onChange(new Date(dateString));
    } else {
      onChange(undefined);
    }
  };

  const handleNativeDateChange = (event: any, selectedDate?: Date) => {
    if (Platform.OS === 'android') {
      setShowPicker(false);
    }
    if (event.type === 'set' && selectedDate) {
      onChange(selectedDate);
    } else if (event.type === 'dismissed') {
      setShowPicker(false);
    }
  };

  const formatDateForInput = (date: Date | undefined) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <View style={styles.container}>
      <ThemedText style={[styles.label, { color: theme.text }]}>{label}</ThemedText>
      
      {Platform.OS === 'web' ? (
        <div style={{ width: '100%' }}>
          <ReactDatePicker
            selected={value}
            onChange={(date: Date | null) => onChange(date || undefined)}
            minDate={minimumDate}
            maxDate={maximumDate}
            dateFormat="dd MMM yyyy"
            placeholderText="Select date"
            isClearable={!!value}
            withPortal
            portalId="root-portal"
            customInput={
              <div
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  paddingLeft: 16,
                  paddingRight: 16,
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                {value ? value.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Select date'}
              </div>
            }
            popperClassName="custom-date-picker-popper"
            calendarClassName="custom-date-picker-calendar"
          />
          <style>{`
            .custom-date-picker-popper {
              z-index: 999999 !important;
            }
            .custom-date-picker-calendar {
              background-color: ${theme.backgroundSecondary};
              border: 1px solid ${theme.border};
              border-radius: 12px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
              z-index: 999999 !important;
            }
            .react-datepicker__header {
              background-color: ${theme.background};
              border-bottom: 1px solid ${theme.border};
              border-top-left-radius: 12px;
              border-top-right-radius: 12px;
              padding-top: 8px;
            }
            .react-datepicker__current-month,
            .react-datepicker__day-name {
              color: ${theme.text};
            }
            .react-datepicker__day {
              color: ${theme.text};
            }
            .react-datepicker__day:hover {
              background-color: ${theme.accent}33;
            }
            .react-datepicker__day--selected,
            .react-datepicker__day--keyboard-selected {
              background-color: ${theme.accent};
              color: #000;
            }
            .react-datepicker__day--disabled {
              color: ${theme.textSecondary};
              opacity: 0.5;
            }
            .react-datepicker__navigation-icon::before {
              border-color: ${theme.text};
            }
            .react-datepicker__close-icon::after {
              background-color: ${theme.textSecondary};
            }
          `}</style>
        </div>
      ) : (
        <>
          <TouchableOpacity
            style={[styles.input, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
            onPress={() => setShowPicker(true)}
          >
            <ThemedText style={[styles.pickerText, { color: value ? theme.text : theme.textSecondary }]}>
              {value ? value.toLocaleDateString() : 'Select date'}
            </ThemedText>
            <Ionicons name="calendar-outline" size={20} color={theme.textSecondary} />
          </TouchableOpacity>
          
          {showPicker && (
            <DateTimePicker
              value={value || new Date()}
              mode="date"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={handleNativeDateChange}
              minimumDate={minimumDate}
              maximumDate={maximumDate}
            />
          )}
          
          {Platform.OS === 'ios' && showPicker && (
            <TouchableOpacity
              style={[styles.doneButton, { backgroundColor: theme.accent }]}
              onPress={() => setShowPicker(false)}
            >
              <ThemedText style={styles.doneText}>Done</ThemedText>
            </TouchableOpacity>
          )}
          
          {value && (
            <TouchableOpacity onPress={() => onChange(undefined)} style={styles.clearDateButton}>
              <ThemedText style={[styles.clearText, { color: theme.error }]}>Clear</ThemedText>
            </TouchableOpacity>
          )}
        </>
      )}
      
      {helperText && (
        <ThemedText style={[styles.helperText, { color: theme.textSecondary }]}>
          {helperText}
        </ThemedText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  webInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
    borderWidth: 1,
    borderRadius: 8,
    paddingRight: 12,
  },
  input: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  pickerText: {
    fontSize: 16,
  },
  clearButton: {
    padding: 4,
  },
  clearDateButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  clearText: {
    fontSize: 14,
  },
  doneButton: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  doneText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
  },
});
